interface UrlLike {
  url: string;
}

interface ImageItemLike<TImg extends UrlLike> {
  readableProblemId: string;
  fImage?: TImg;
  problemImages?: TImg[];
  layoutType?: number;
  headerLabel?: string;
}

/** 画像 URL から `_l\d` suffix を抽出（layoutType）。なければ null */
export function extractLayoutTypeFromImageUrl(imageUrl: string | undefined | null): number | null {
  if (!imageUrl) return null;
  const m = imageUrl.match(/_l(\d)/);
  return m ? parseInt(m[1], 10) : null;
}

/**
 * imageItems のいずれかの画像が `_l\d` suffix を持つか判定（混在描画モードのトリガ）。
 * imageItems はモード別（article=問題画像 / spellcheck=解答画像）に渡されるため、
 * 本判定は問題・解答ごと独立に評価される。
 */
export function hasMixedLayoutImages<TImg extends UrlLike, TItem extends ImageItemLike<TImg>>(items: TItem[]): boolean {
  for (const item of items) {
    const imgs = item.problemImages || [];
    for (const img of imgs) {
      if (extractLayoutTypeFromImageUrl(img.url) !== null) return true;
    }
  }
  return false;
}

/**
 * imageItems を画像 URL `_q\d{2}_` / `_a\d{2}_` の daimonId 単位に分割し、
 * 各 group の代表画像から `_l\d` を抽出 → layoutType をそのまま割り当てる。
 *
 * - hasMixedLayoutImages(items) が false なら完全 noop（参照そのまま返却）
 * - layoutType 抽出失敗 group は元の layoutType を維持（フォールバック）
 *
 * 副次効果: items を daimon 単位に分割することで .print-page (page-break-after: always)
 * が daimon ごとに適用され、印刷時の大問改ページが自動的に実現される。
 */
export function splitImageItemsByMixedLayout<TImg extends UrlLike, TItem extends ImageItemLike<TImg>>(
  items: TItem[],
  menuType: 'article' | 'spellcheck'
): TItem[] {
  if (!hasMixedLayoutImages(items)) {
    return items;
  }
  const daimonRe = menuType === 'article' ? /_q(\d{2})_/ : /_a(\d{2})_/;
  const result: TItem[] = [];
  for (const item of items) {
    const imgs = item.problemImages || [];
    if (imgs.length === 0) {
      result.push(item);
      continue;
    }
    // 画像はサーバから daimon 順で渡ってくる前提で、daimonId が切り替わる境界のみで
    // group を分割し、元の並び順をそのまま保持する。
    // Map + keys.sort() 方式だと、daimon 抽出できない画像 (例: no-picture-type-N.png) が
    // 'unknown' キーに集約された上で末尾へソートされ、本来の位置から飛ばされてしまう。
    // また同一 paper 内に複数の no-picture がある場合、全て 1 group に潰れてしまう。
    // 連続ラン方式にすることで並び順を保持し、daimon 抽出不可の画像も各々独立 group にする。
    const groups: { daimonKey: string; imgs: TImg[] }[] = [];
    for (const img of imgs) {
      const m = img.url.match(daimonRe);
      const daimonKey = m ? m[1] : null;
      const prev = groups[groups.length - 1];
      if (daimonKey !== null && prev && prev.daimonKey === daimonKey) {
        prev.imgs.push(img);
      } else {
        // daimon 抽出不可の画像は readableProblemId を一意に保つため index ベースのキーを付与
        groups.push({ daimonKey: daimonKey ?? `x${groups.length}`, imgs: [img] });
      }
    }
    groups.forEach((group, i) => {
      const groupImgs = group.imgs;
      const overridden = extractLayoutTypeFromImageUrl(groupImgs[0].url);
      const newItem: TItem = {
        ...item,
        readableProblemId: i === 0 ? item.readableProblemId : `${item.readableProblemId}__ml_d${group.daimonKey}`,
        layoutType: overridden ?? item.layoutType,
        problemImages: groupImgs,
        fImage: i === 0 ? item.fImage : undefined
      };
      result.push(newItem);
    });
  }
  return result;
}

/**
 * splitImageItemsByMixedLayout 後の items を走査し、連続する layoutType=3 の大問を
 * 1 アイテムに結合する（画像配列を連結）。layoutType 1/2 は対象外。
 *
 * Why: layoutType 3 は 2 列グリッド + コンテナ間 separater が「従来の表示ルール」。
 * 画面表示時に大問単位で分けると separater が大問の境目に入らず、見た目が崩れるため、
 * 連続する layoutType 3 大問は 1 ブロックに結合し、calcImageLayout のペアリングと
 * 既存 separater で自然に区切られるようにする。印刷側は大問単位の改ページが必要なので
 * 呼び出さない。
 *
 * 混在 paper 以外（trial 用 bookmark 印刷など、同一 layoutType の複数 imageItem を
 * 別文脈で生成しているケース）で誤動作しないよう、hasMixedLayoutImages=false なら noop。
 */
export function mergeConsecutiveLayoutType3<TImg extends UrlLike, TItem extends ImageItemLike<TImg>>(items: TItem[]): TItem[] {
  if (!hasMixedLayoutImages(items)) return items;
  const result: TItem[] = [];
  for (const item of items) {
    const prev = result[result.length - 1];
    if (prev && prev.layoutType === 3 && item.layoutType === 3) {
      result[result.length - 1] = {
        ...prev,
        problemImages: [...(prev.problemImages || []), ...(item.problemImages || [])]
      };
    } else {
      result.push(item);
    }
  }
  return result;
}
